import PropertiesMap from '@/components/properties/PropertiesMap';
import PropertyCard from '@/components/properties/PropertyCard';
import { Card } from '@/components/ui/card';
import { MotionButton } from '@/components/ui/motion-button';
import { usePropertyQueries } from '@/hooks/queries/usePropertyQueries';
import { slideInFromBottom, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { Property } from '@/types/property';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Home, LayoutGrid, MapIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_public/properties/')({
  component: PropertiesListPage,
})

// Empty state component for when no properties are found
function NoPropertiesFound() {
  return (
    <Card className="p-8 flex flex-col items-center">
      <Home className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-xl font-semibold">No Properties Found</h3>
    </Card>
  );
}

export default function PropertiesListPage() {
  useEffect(() => {
    document.title = 'Properties | Swim Homes';
  }, []);

  const [view, setView] = useState<'map' | 'list'>('list');
  const [hoveredListing, setHoveredListing] = useState<string | null>(null);

  const navigate = useNavigate();
  const { useFetchActiveProperties } = usePropertyQueries();
  const { data: properties } = useFetchActiveProperties();

  const handleListingClick = (property: Property) => {
    navigate({ to: `/properties/$id`, params: { id: property.property_id } });
  };

  const hasProperties = properties.length > 0;

  return (
    <>
      <div className="bg-background pb-12 relative z-10">
        {/* Header section */}
        <div className="bg-linear-to-b from-gray-50 to-white py-12">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOptions}
            >
              <motion.div
                variants={staggerItem}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Find Your Perfect Home</h1>
                  <p className="mt-2 text-lg text-gray-600">Browse our selection of quality properties across Ontario</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Mobile view toggle */}
        <div className="md:hidden sticky top-16 z-20 bg-background border-b p-4 flex gap-2">
          <MotionButton
            variant={view === 'map' ? 'default' : 'outline'}
            className="flex-1"
            onClick={() => setView('map')}
          >
            <MapIcon className="h-4 w-4 mr-2" />
            Map
          </MotionButton>
          <MotionButton
            variant={view === 'list' ? 'default' : 'outline'}
            className="flex-1"
            onClick={() => setView('list')}
          >
            <LayoutGrid className="h-4 w-4 mr-2" />
            List
          </MotionButton>
        </div>

        {/* Main content */}
        <div className="max-w-7xl mx-auto px-4 pt-8">
          {/* Desktop layout */}
          <div className="hidden md:flex gap-6 w-full">
            {/* Property listing column */}
            <div className="w-3/5 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {hasProperties ? (
                properties.map((property, index) => (
                  <motion.div
                    key={property.property_id}
                    variants={slideInFromBottom}
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportOptions}
                    transition={{ delay: index * 0.1 }}
                  >
                    <PropertyCard
                      property={property}
                      onMouseEnter={() => setHoveredListing(property.property_id)}
                      onMouseLeave={() => setHoveredListing(null)}
                      onClick={() => handleListingClick(property)}
                    />
                  </motion.div>
                ))
              ) : (
                <div className="col-span-2 py-12 text-center">
                  <NoPropertiesFound />
                </div>
              )}
            </div>

            {/* Map column */}
            <div className="w-2/5">
              <div className="sticky top-40 h-[calc(100vh-16rem)]">
                <PropertiesMap
                  properties={properties}
                  hoveredListing={hoveredListing}
                />
              </div>
            </div>
          </div>

          {/* Mobile layout */}
          <div className="md:hidden w-full">
            {view === 'map' ? (
              <div className="h-[calc(100vh-15rem)]">
                <PropertiesMap
                  properties={properties}
                  hoveredListing={hoveredListing}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {hasProperties ? (
                  properties.map((property, index) => (
                    <motion.div
                      key={property.property_id}
                      variants={slideInFromBottom}
                      initial="hidden"
                      whileInView="visible"
                      viewport={viewportOptions}
                      transition={{ delay: index * 0.1 }}
                    >
                      <PropertyCard
                        property={property}
                        onMouseEnter={() => setHoveredListing(property.property_id)}
                        onMouseLeave={() => setHoveredListing(null)}
                        onClick={() => handleListingClick(property)}
                      />
                    </motion.div>
                  ))
                ) : (
                  <div className="py-12 text-center">
                    <NoPropertiesFound />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
